import React from 'react';
import useAuth from '../../hooks/useAuth';

type PermissionType = 'view' | 'create' | 'edit' | 'delete';

interface RoleBasedRendererProps {
  children: React.ReactNode;
  requiredPermission?: PermissionType;
  requiredRoles?: string[];
  fallback?: React.ReactNode;
}

/**
 * A component that conditionally renders its children based on the user's role
 * 
 * @param {React.ReactNode} children - The content to show if the user has permission
 * @param {PermissionType} requiredPermission - The required permission type ('view', 'create', 'edit', 'delete')
 * @param {string[]} requiredRoles - An array of roles that are allowed to view the children
 * @param {React.ReactNode} fallback - Optional content to show if the user doesn't have permission
 */
const RoleBasedRenderer: React.FC<RoleBasedRendererProps> = ({
  children,
  requiredPermission,
  requiredRoles,
  fallback = null
}) => {
  const { canView, canCreate, canEdit, canDelete, hasRole } = useAuth();
  
  // Check if user has the required permission
  const hasPermission = (() => {
    if (!requiredPermission) return true;
    
    switch (requiredPermission) {
      case 'view': return canView;
      case 'create': return canCreate;
      case 'edit': return canEdit;
      case 'delete': return canDelete;
      default: return false;
    }
  })();
  
  // Check if user has one of the required roles (if specified)
  const hasRequiredRole = requiredRoles ? hasRole(requiredRoles) : true;
  
  // User must have both the required permission AND be in the allowed roles list (if specified)
  const isAuthorized = hasPermission && hasRequiredRole;
  
  return isAuthorized ? <>{children}</> : <>{fallback}</>;
};

export default RoleBasedRenderer; 